import React, { useEffect, useRef, useState } from "react";
import { MentionedAsset } from "../hooks/useAtMention";
import { AssetPickerList, renderMentionChipInner } from "../../AssetSearch/AssetPickerList";

// The row renderer moved to AssetSearch/AssetPickerList with the list; re-exported so the mention editor and node keep their import
export { renderMentionChipInner };

interface AtMentionPickerProps {
  isOpen: boolean;
  assets: MentionedAsset[];
  query: string;
  onSelect: (asset: MentionedAsset) => void;
  onClose: () => void;
}

// The chat's @-mention picker: the shared AssetPickerList floated above the editor, driven by window-level keys because focus stays in the editor, with a description tooltip beside the hovered row.
const AtMentionPicker: React.FC<AtMentionPickerProps> = ({
  isOpen,
  assets,
  query,
  onSelect,
  onClose,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredAsset, setHoveredAsset] = useState<MentionedAsset | null>(null);
  const [tooltipTop, setTooltipTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, assets.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if ((e.key === "Enter" || e.key === "Tab") && assets[activeIndex]) {
        e.preventDefault();
        onSelect(assets[activeIndex]);
      } else if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, assets, activeIndex, onSelect, onClose]);

  if (!isOpen) return null;

  const handleHoverItem = (asset: MentionedAsset | null, element: HTMLLIElement | null) => {
    if (asset?.description && element) {
      const containerRect = containerRef.current?.getBoundingClientRect();
      const itemRect = element.getBoundingClientRect();
      setTooltipTop(itemRect.top - (containerRect?.top ?? 0));
      setHoveredAsset(asset);
    } else {
      setHoveredAsset(null);
    }
  };

  return (
    <div ref={containerRef} className="absolute bottom-full left-0 mb-1 z-50 w-full sm:w-1/2">
      <AssetPickerList
        assets={assets}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onSelect={onSelect}
        onHoverItem={handleHoverItem}
        emptyLabel={query ? `No assets matching "${query}"` : "Loading…"}
        tabHint
        className="max-h-52"
      />

      {hoveredAsset?.description && (
        <div
          className="eai-card pointer-events-none absolute left-full ml-1 hidden w-52 px-3 py-2 text-eai-body-xs text-eai-ink sm:block"
          style={{ top: tooltipTop }}
        >
          {hoveredAsset.description}
        </div>
      )}
    </div>
  );
};

export default AtMentionPicker;
